/**
 * Entitlement-gate affordances for the encyclopedia.
 *
 * Anonymous / non-purchaser visitors may read only open-canon content; Grymwyrd
 * (and official/homebrew) content is shown but **locked** — a small padlock on
 * cards, and a full gate panel in place of detail/results — leading to the
 * branded buy page. Owners (Atlas entitlement) never see these.
 *
 * `LockBadge` — the small padlock for cards/search rows.
 * `LockedGate` — the full "sealed, unlock with Grymwyrd Cataclysm" panel.
 * `unlockHref` — where a lock leads (the branded buy/unlock page).
 */
import { Lock, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui'
import { useT, LocaleLink } from '@/i18n'
import { useUserStore } from '@/stores/userStore'
import { cn } from '@/lib/cn'
import { requiredBookFor, type BookGateInput } from '@/lib/entitlements/contentAccess'

/**
 * The branded buy/unlock destination for locked content. Routes to the gating
 * book's detail page (which carries the `BuyButton` → Atlas-brokered checkout),
 * tagged with the originating content so we can return the visitor after
 * purchase. See `features/books/BookDetailPage`.
 */
export function unlockHref(sel: BookGateInput = {}, from?: string): string {
  const book = requiredBookFor(sel)
  return from ? `/books/${book}?unlock=${encodeURIComponent(from)}` : `/books/${book}?unlock=1`
}

/** Small padlock badge for locked cards / search rows. */
export function LockBadge({ className }: { className?: string }) {
  const t = useT()
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm border border-gold/40 bg-obsidian/80 px-1.5 py-0.5 font-ui text-[10px] uppercase tracking-wide text-gold backdrop-blur-sm',
        className,
      )}
      title={t('encyclopedia.lockBadge.title')}
    >
      <Lock size={11} aria-hidden />
      {t('encyclopedia.lockBadge.label')}
    </span>
  )
}

/**
 * Full gate panel shown in place of locked content (detail page or a locked
 * facet selection). Drives the account/purchase funnel: buy, or sign in if the
 * visitor may already own it (e.g. a backer).
 */
export function LockedGate({
  selection = {},
  from,
  title,
  className,
}: {
  selection?: BookGateInput
  from?: string
  title?: string
  className?: string
}) {
  const t = useT()
  const authenticated = useUserStore((s) => s.status === 'authenticated')
  const href = unlockHref(selection, from)
  return (
    <section
      className={cn('surface-parchment mx-auto max-w-xl p-8 text-center', className)}
      aria-label={t('encyclopedia.lockedGate.regionAria')}
    >
      <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-gold/40 bg-obsidian/60">
        <Lock size={26} className="text-gold" aria-hidden />
      </span>
      <h2 className="title-gothic text-2xl text-bone">{title ?? t('encyclopedia.lockedGate.defaultTitle')}</h2>
      <p className="mx-auto mt-3 max-w-md font-body text-bone-muted">
        {t('encyclopedia.lockedGate.body')}
      </p>
      <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <LocaleLink to={href}>
          <Button variant="gold" size="lg">
            <Sparkles size={18} aria-hidden />
            {t('encyclopedia.lockedGate.unlockCta')}
          </Button>
        </LocaleLink>
        {!authenticated && (
          <LocaleLink to="/login">
            <Button variant="metal" size="lg">
              {t('encyclopedia.lockedGate.signInCta')}
            </Button>
          </LocaleLink>
        )}
      </div>
      {!authenticated && (
        <p className="mt-4 font-body text-xs text-bone-dim">
          {t('encyclopedia.lockedGate.backerNote')}
        </p>
      )}
    </section>
  )
}
